export const emptyInstallment = () => ({
  amount: '',
  expectedOn: '',
  paymentTrigger: '',
});

export const errorMessage = (error, fallback) =>
  error?.response?.data?.errors?.[0] ||
  error?.response?.data?.message ||
  fallback;

export const moneyCents = value => {
  const source = String(value || '').replace(/[R$\s]/g, '');
  const normalized = source.includes(',')
    ? source.replaceAll('.', '').replace(',', '.')
    : source;
  const numericAmount = Number(normalized);
  return Number.isFinite(numericAmount) ? Math.round(numericAmount * 100) : 0;
};

export const amountFromCents = value => (Number(value || 0) / 100).toFixed(2);

export const formFromPlan = plan => ({
  grossAmount: plan?.valor_comissao_total?.amount || '',
  deduction: plan?.deducao?.amount || '',
  modality: plan?.comissao || 'integral',
  paymentMethod: plan?.liquida || '',
  notes: plan?.observacoes || '',
  installments: plan?.parcelas_empresa?.map(installment => ({
    amount: installment.valor_parcela?.amount || '',
    expectedOn: installment.dia_pagamento || '',
    paymentTrigger:
      installment.payment_trigger || installment.periodo_pagamento || '',
  })) || [emptyInstallment()],
});

export const commissionPlanPayload = form => ({
  gross_amount: form.grossAmount,
  deduction: form.deduction,
  modality: form.modality,
  payment_method: form.paymentMethod,
  notes: form.notes,
  installments: form.installments.map(installment => ({
    amount: installment.amount,
    expected_on: installment.expectedOn,
    payment_trigger: installment.paymentTrigger,
  })),
});

export const commissionPlanTotals = form => {
  const grossCents = moneyCents(form.grossAmount);
  const deductionCents = moneyCents(form.deduction);
  const installmentTotalCents = form.installments.reduce(
    (total, installment) => total + moneyCents(installment.amount),
    0
  );
  return {
    grossCents,
    deductionCents,
    netCents: grossCents - deductionCents,
    installmentTotalCents,
  };
};

export const canSaveCommissionPlan = ({ form, installmentsMatch }) => {
  const { grossCents, deductionCents } = commissionPlanTotals(form);
  return (
    grossCents > deductionCents &&
    Boolean(form.paymentMethod) &&
    form.installments.length > 0 &&
    installmentsMatch &&
    form.installments.every(installment => installment.paymentTrigger)
  );
};

export const validateCommissionPlan = ({ form, t }) => {
  const { grossCents, deductionCents, installmentTotalCents, netCents } =
    commissionPlanTotals(form);
  const errors = {};

  if (grossCents <= 0)
    errors.grossAmount = t('REAL_ESTATE_COMMISSION_PLAN.ERRORS.GROSS');
  if (deductionCents >= grossCents)
    errors.deduction = t('REAL_ESTATE_COMMISSION_PLAN.ERRORS.DEDUCTION');
  if (!form.paymentMethod)
    errors.paymentMethod = t(
      'REAL_ESTATE_COMMISSION_PLAN.ERRORS.PAYMENT_METHOD'
    );
  if (!form.installments.length)
    errors.installments = t(
      'REAL_ESTATE_COMMISSION_PLAN.ERRORS.INSTALLMENTS_REQUIRED'
    );
  if (installmentTotalCents !== netCents)
    errors.installments = t(
      'REAL_ESTATE_COMMISSION_PLAN.ERRORS.INSTALLMENTS_TOTAL'
    );
  if (form.installments.some(installment => !installment.paymentTrigger))
    errors.installments = t(
      'REAL_ESTATE_COMMISSION_PLAN.ERRORS.PERIOD_REQUIRED'
    );

  return { errors, valid: Object.keys(errors).length === 0 };
};
